import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { checkShopOrigin, fetchSelfies } from '../utils/apiService';
import { CHECK_MESSAGE, isLoggedOutError, notifySession, parseConnectParams, readSelfies } from '../utils/connectFlow';

/**
 * A hidden frame on a shop's page that tells the shop, and only the shop whose key it carries,
 * whether this browser is logged in to KuzFit right now: on load and whenever the shop asks. It
 * asks the KuzFit login itself each time (not a cached token), so a logout anywhere shows at once,
 * and hands the shop the shopper's fresh Auth0 access token for its try-on images.
 * When it cannot tell (any error other than "log in"), it says nothing.
 */
const canSeeLogin = async () => {
  try {
    return typeof document.hasStorageAccess === 'function' ? await document.hasStorageAccess() : true;
  } catch (e) {
    return false;
  }
};

const Session = () => {
  const { isLoading, getAccessTokenSilently } = useAuth0();
  const { shopKey, origin, valid } = useMemo(() => parseConnectParams(window.location.search), []);
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (!valid) return;
    checkShopOrigin(shopKey, origin).then(setAllowed).catch(() => setAllowed(false));
  }, [valid, shopKey, origin]);

  const check = useCallback(async () => {
    let token;
    try {
      token = await getAccessTokenSilently({ cacheMode: 'off' });
    } catch (e) {
      // Where the browser hides the KuzFit login from frames, "log in" only means it cannot see.
      if (isLoggedOutError(e) && (await canSeeLogin())) notifySession(window.parent, origin, { signedIn: false });
      return;
    }
    const selfies = await fetchSelfies(token).then(readSelfies).catch(() => ({ active: null }));
    notifySession(window.parent, origin, { signedIn: true, token, selfie: selfies.active });
  }, [getAccessTokenSilently, origin]);

  useEffect(() => {
    if (!allowed || isLoading) return undefined;
    check();
    const onMessage = (event) => {
      if (event.origin === origin && event.data && event.data.type === CHECK_MESSAGE) check();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [allowed, isLoading, check, origin]);

  return null;
};

export default Session;
